import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './News.css';

const SOURCE_URL = 'https://presto-ferry-g7bz.here.now/';

function formatEdition(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}

// "@handle" for an X post, otherwise the site's hostname.
function linkLabel(link) {
    const { hostname, pathname } = new URL(link);
    const host = hostname.replace(/^www\./, '');
    const handle = pathname.split('/')[1];
    return host === 'x.com' && handle ? `@${handle}` : host;
}

const ExternalLink = ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
);

const News = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetch('/api/news', { credentials: 'include' })
            .then(r => {
                if (!r.ok) throw new Error('Failed to load news');
                return r.json();
            })
            .then(data => setItems(data))
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, []);

    const pulses = useMemo(() => {
        const bySlug = new Map();
        items.forEach(item => {
            if (!bySlug.has(item.pulse)) {
                bySlug.set(item.pulse, {
                    slug: item.pulse,
                    name: item.pulse_name.replace(/ Pulse$/, ''),
                    editionAt: item.edition_at,
                });
            }
        });
        return [...bySlug.values()];
    }, [items]);

    const selected = pulses.find(p => p.slug === searchParams.get('pulse')) || pulses[0];
    const stories = selected ? items.filter(item => item.pulse === selected.slug) : [];

    // On phones the topics scroll sideways, so a pulse opened from the URL can start off-screen.
    const activePulseRef = useRef(null);
    useEffect(() => {
        activePulseRef.current?.scrollIntoView?.({ block: 'nearest', inline: 'center' });
    }, [selected?.slug]);

    return (
        <div className="news">
            <div className="news-header">
                <h1>News</h1>
                {selected?.editionAt && (
                    <span className="news-edition">Edition of {formatEdition(selected.editionAt)}</span>
                )}
            </div>

            {loading && <p>Loading news…</p>}
            {error && <div className="alert-error">{error}</div>}

            {selected && (
                <>
                    <div className="news-pulses" role="group" aria-label="Topic">
                        {pulses.map(pulse => (
                            <button
                                key={pulse.slug}
                                ref={pulse.slug === selected.slug ? activePulseRef : null}
                                className={`news-pulse ${pulse.slug === selected.slug ? 'active' : ''}`}
                                aria-pressed={pulse.slug === selected.slug}
                                onClick={() => setSearchParams({ pulse: pulse.slug }, { replace: true })}
                            >
                                {pulse.name}
                            </button>
                        ))}
                    </div>

                    <ol className="news-list">
                        {stories.map(story => (
                            <li key={story.id} className="news-story">
                                <h2><ExternalLink href={story.url}>{story.title}</ExternalLink></h2>
                                {story.section && <span className="news-section">{story.section}</span>}
                                {story.synopsis && <p className="news-synopsis">{story.synopsis}</p>}
                                {story.why_it_matters && (
                                    <p className="news-why"><strong>Why it matters:</strong> {story.why_it_matters}</p>
                                )}
                                {story.also.length > 0 && (
                                    <p className="news-also">
                                        Also:{' '}
                                        {story.also.map((link, i) => (
                                            <React.Fragment key={link}>
                                                {i > 0 && ' · '}
                                                <ExternalLink href={link}>{linkLabel(link)}</ExternalLink>
                                            </React.Fragment>
                                        ))}
                                    </p>
                                )}
                            </li>
                        ))}
                    </ol>

                    <p className="news-credit">
                        Headlines and summaries from <ExternalLink href={SOURCE_URL}>Scobleizer News Pulse</ExternalLink>.
                    </p>
                </>
            )}
        </div>
    );
};

export default News;
